import { Col, Row, Tag } from "antd"
import dayjs from "dayjs"
import React from "react"
import { useTranslation } from "react-i18next"

import { WorkspaceCard } from "../../../Lead/LeadWorkspacePage/cards/WorkspaceCard.tsx"
import {
  customerInvitationStatusInfo,
  customerStatusColorMap
} from "../../constants/customer-statuses.ts"
import { type Customer } from "../../types/customer.types.ts"
import { CustomerDetailField } from "../CustomerDetailField.tsx"

const formatDate = (value?: string | null) =>
  value ? dayjs(value).format("MMM D, YYYY") : null

function CustomerAccountCard({ customer }: { customer: Customer }) {
  const { t } = useTranslation()

  const status = customer?.status?.trim()
  // The Customers list already relabels the raw backend value (e.g. "no
  // access" -> "Not Invited") via this same map — this card fell out of
  // sync with it and kept showing the raw string. A status the map
  // doesn't recognize still falls back to the raw value, same as the list.
  const normalizedStatus = status?.toLowerCase()
  const statusLabel = normalizedStatus
    ? (customerInvitationStatusInfo[normalizedStatus]?.label ?? status)
    : status
  const counts = customer?.project_count

  return (
    <WorkspaceCard cardId={"account"} title={t("customerDetail.cards.account")}>
      <Row gutter={[12, 8]}>
        <Col xs={24} sm={12}>
          <CustomerDetailField
            label={t("customerDetail.fields.status")}
            value={status}
          >
            <Tag
              color={
                customerStatusColorMap[
                  normalizedStatus as keyof typeof customerStatusColorMap
                ]
              }
            >
              {statusLabel}
            </Tag>
          </CustomerDetailField>
        </Col>
        <Col xs={24} sm={12}>
          <CustomerDetailField
            label={t("customerDetail.fields.registeredAt")}
            value={formatDate(customer?.registered_at)}
          />
        </Col>
        <Col xs={24} sm={12}>
          <CustomerDetailField
            label={t("customerDetail.fields.created")}
            value={formatDate(customer?.created)}
          />
        </Col>
        <Col xs={24} sm={12}>
          <CustomerDetailField
            label={t("customerDetail.fields.projects")}
            value={counts ? String(counts.total ?? 0) : null}
          >
            <span data-testid={"customer-project-counts"}>
              {t("customerDetail.counts.summary", {
                total: counts?.total ?? 0,
                lead: counts?.lead ?? 0,
                active: counts?.active ?? 0,
                archived: counts?.archived ?? 0
              })}
            </span>
          </CustomerDetailField>
        </Col>
      </Row>
    </WorkspaceCard>
  )
}

export { CustomerAccountCard }
